import React, { useEffect, useState, useContext } from 'react';
import { Box, Paper, Typography, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { sortClaims } from 'src/selectors/claimsSelector';
import { formatAmountToISKCurrencyString } from 'src/helpers/formatAmountToString';
import { Error } from 'src/components/Banners';
import { loadOpenClaims } from 'src/api/claimApi';
import { loadActiveAgreements } from 'src/api/agreementApi';
import PayerClaims from 'src/containers/OpenClaims/components/PayerClaims';
import InfoBanner from 'src/components/MainLayout/InfoBanner';
import Link from 'next/link';
import { AuthContext } from 'src/context/AuthProvider';
import NotificationBanner from 'src/components/MainLayout/NotificationBanner';
import Router from 'next/router';
import { loadEmails, loadDefaults } from 'src/api/payerApi';
import { getDigitalCollectionNotification } from 'src/helpers/notificationProvider';
import { setPaymentBlockingClaims } from 'src/helpers/claimHelper';
import Agreements from './greidslusamkomulag';
import PayerClaimsActions from 'src/containers/OpenClaims/components/PayerClaimsActions';
import CreateIssueSideBar from 'src/containers/CreateIssueSideBar';
import ClaimantActions from 'src/containers/OpenClaims/components/ClaimantActions';
import Banner from 'src/components/Banner/Banner';
import { checkAgreementBanners, PENDING_CANCELLATION } from 'src/helpers/agreementHelpers';

const typographyStyles = {
  pageTitle: {
    variant: 'h2',
    sx: {
      mt: '70px',
      p: 0,
      fontSize: {
        xs: '32px',
        sm: '40px',
      },
      fontWeight: 500,
    },
  },
  subtitle: {
    variant: 'body1',
    sx: {
      mt: '24px',
      fontSize: '20px',
    },
  },
  sectionTitle: {
    variant: 'h2',
    sx: {
      mt: {
        xs: '25px',
        sm: '40px',
      },
      mb: '10px',
      p: '8px',
      fontSize: {
        xs: '21px',
        sm: '32px',
      },
      fontWeight: 500,
    },
  },
  linkText: {
    variant: 'body2',
    sx: {
      fontSize: '20px',
      color: '#325127',
    },
  },
};

const PayerClaimsSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    <Box sx={{ mb: 3 }}>
      <Skeleton variant="text" width="30%" height={40} />
      <Skeleton variant="text" width="20%" height={30} sx={{ mt: 1 }} />
    </Box>
    {[1, 2, 3].map((index) => (
      <Paper elevation={1} key={index} sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="20%" />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width={100} height={20} />
          <Skeleton variant="rectangular" width={100} height={20} />
          <Skeleton variant="rectangular" width={100} height={20} />
        </Box>
      </Paper>
    ))}
  </Box>
);

export default function OpenClaims() {
  const [claims, setClaims] = useState([]);
  const [claimants, setClaimants] = useState([]);
  const [agreements, setAgreements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationError, setOperationError] = useState(null);
  const [filterBy] = useState('');
  const [notificationProps, setNotificationProps] = useState(null);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [isIssueDrawerOpen, setIsIssueDrawerOpen] = useState(false);
  const [preSelectedIssueType, setPreSelectedIssueType] = useState(null);
  const [shouldShowAgreementSuccessBanner, setShouldShowAgreementSuccessBanner] = useState(false);
  const [shouldShowClosingReasonBanner, setShouldShowClosingReasonBanner] = useState(false);
  const [shouldShowCounterOfferBanner, setShouldShowCounterOfferBanner] = useState(false);
  const [shouldShowRejectedBanner, setShouldShowRejectedBanner] = useState(false);
  const [shouldShowOverdueInstallmentBanner, setShouldShowOverdueInstallmentBanner] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sortCriteria, setSortCriteria] = useState({
    claimsOrderDesc: false,
    claimsOrderBy: 'oldestUnpaidDueDate',
  });

  const user = useContext(AuthContext);

  useEffect(() => {
    setClaimants([...new Set(claims.map((x) => x.claimantName))]);
  }, [claims]);

  useEffect(() => {
    if (Router.query.rejected === 'true') {
      setShouldShowRejectedBanner(true);
      const cleanUrl = '/opinmal';
      Router.replace(cleanUrl, undefined, { shallow: true });
    }
  }, []);

  useEffect(() => {
    function onSuccess(data) {
      setError(null);
      const processedClaims = data.claims.map((x) => setPaymentBlockingClaims(x, data.claims));
      setClaims(sortClaims(processedClaims, sortCriteria));
      setIsLoading(false);
    }

    function onError() {
      setError({
        message: 'Mistókst að sækja opin mál. Vinsamlegast endurhlaðið síðuna.',
      });
      setIsLoading(false);
    }

    function onAgreementsSuccess(data) {
      const nonClosedAgreements = data.filter((agreement) => agreement.status !== 'Closed');
      setAgreements(nonClosedAgreements);

      const bannerStates = checkAgreementBanners(data);

      setShouldShowAgreementSuccessBanner(bannerStates.shouldShowAgreementSuccessBanner);
      setShouldShowClosingReasonBanner(bannerStates.shouldShowClosingReasonBanner);
      setShouldShowCounterOfferBanner(bannerStates.shouldShowCounterOfferBanner);
      setShouldShowOverdueInstallmentBanner(bannerStates.shouldShowOverdueInstallmentBanner);
    }

    loadOpenClaims(setIsLoading, onSuccess, onError);
    loadActiveAgreements(onAgreementsSuccess);
    loadDefaults((data) => setDefaults(data), () => {});
  }, []);

  useEffect(() => {
    setClaims((prevState) => [...sortClaims(prevState, sortCriteria)]);
  }, [sortCriteria]);

  useEffect(() => {
    if (user.settings !== null) {
      loadEmails(
        (data) =>
          setNotificationProps(
            getDigitalCollectionNotification(data, Router.query, user.settings.digitalCollectionAccepted)
          ),
        () => {}
      );
    }
  }, [user]);

  const handleSelectedClaimsChange = (newSelected) => {
    setSelectedClaims(newSelected);
  };

  const totalRemaining = claims.reduce((acc, claim) => acc + claim.totalRemaining, 0);

  return (
    <>
      <Typography {...typographyStyles.pageTitle}>Settu málin í réttan farveg</Typography>
      <Typography {...typographyStyles.subtitle}>
        Hér á mínum síðum færðu yfirsýn yfir kröfur í innheimtu hjá Motus og Lögheimtunni. Við hvetjum þig til að greiða
        eða semja um greiðslu þeirra sem allra fyrst til að komast hjá{' '}
        <Link href="https://motus.is/hvernig-reiknast-kostnadur-a-innheimtumal/" passHref target="_blank">
          <Typography {...typographyStyles.linkText} component="span">
            kostnaðarsömum innheimtuaðgerðum
          </Typography>
        </Link>
        .{' '}
        <Typography sx={{ fontSize: '20px' }} component="span">
          Ekki gera ekki neitt.
        </Typography>
      </Typography>

      <Grid container spacing={2}>
        <InfoBanner />
        <NotificationBanner notification={notificationProps} />

        {defaults.length > 0 && (
          <Grid xs={12} sm={12} sx={{ mt: 4 }}>
            <Banner type="error" isDismissible={false}>
              <Typography variant="body2" fontSize="16px">
                Mál þetta verður eða hefur verið sent á vanskilaskrá Creditinfo, sjá nánar á{' '}
                <Link href="https://mitt.creditinfo.is/" passHref target="_blank">
                  <Typography component="span" sx={{ fontSize: '16px', color: '#325127', textDecoration: 'underline' }}>
                    mitt.creditinfo
                  </Typography>
                </Link>
                . Á meðan að málið er með stöðuna &bdquo;<strong>upphaf löginnheimta</strong>&ldquo; getur þú greitt eða samið um
                málið hér á vefnum og verður skráning þá afskráð af vanskilaskránni. Þegar málið hefur fengið stöðuna &bdquo;
                <strong>löginnheimta</strong>&ldquo; þarf að greiða málið að fullu svo það verði afskráð af vanskilaskrá.{' '}
                <Link href="https://www.creditinfo.is/einstaklingar/vanskil" passHref target="_blank">
                  <Typography component="span" sx={{ fontSize: '16px', color: '#325127', textDecoration: 'underline' }}>
                    Sjá nánar um áhrif vanskilaskráninga
                  </Typography>
                </Link>
                .
              </Typography>
            </Banner>
          </Grid>
        )}

        {!isLoading && (
          <>
            {shouldShowRejectedBanner && (
              <Grid xs={12} sm={12} sx={{ mt: 4 }}>
                <Banner
                  type="info"
                  isDismissible
                  onDismiss={() => setShouldShowRejectedBanner(false)}
                  sx={{ alignItems: 'center' }}
                >
                  <Typography variant="body2" fontSize="16px">
                    Þú hefur hafnað tillögu okkar að greiðslusamkomulagi. Upplýsingar um samkomulagið eru því ekki
                    lengur aðgengilegar.
                  </Typography>
                </Banner>
              </Grid>
            )}
            {shouldShowAgreementSuccessBanner && (
              <Grid xs={12} sm={12} sx={{ mt: 4 }}>
                <Banner type="success" isDismissible={false} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" fontSize="16px">
                    Beiðni þín um greiðslusamkomulag hefur verið samþykkt. Þú getur fylgst með stöðu samkomulagsins hér
                    á vefnum.
                  </Typography>
                </Banner>
              </Grid>
            )}
            {shouldShowClosingReasonBanner && (
              <Grid xs={12} sm={12} sx={{ mt: 4 }}>
                <Banner type="info" isDismissible={false} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" fontSize="16px">
                    Beiðni um greiðslusamkomulag hefur verið hafnað. Motus hefur ekki heimild til að gera samkomulag
                    fyrir hönd kröfuhafa um þetta mál. Við munum senda þér tölvupóst með frekari upplýsingum.
                  </Typography>
                </Banner>
              </Grid>
            )}
            {shouldShowCounterOfferBanner && (
              <Grid xs={12} sm={12} sx={{ mt: 4 }}>
                <Banner type="info" isDismissible={false} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" fontSize="16px">
                    Beiðni þín um greiðslusamkomulag hefur verið uppfærð í samræmi við það svigrúm sem kröfuhafi getur
                    boðið. Vinsamlegast skoðaðu uppfært tilboð, þú þarft að samþykkja innan 48 klukkustunda til að
                    samkomulagið taki gildi. Að öðrum kosti fellur tilboðið niður.
                  </Typography>
                </Banner>
              </Grid>
            )}
            {shouldShowOverdueInstallmentBanner && (
              <Grid xs={12} sm={12} sx={{ mt: 4 }}>
                <Banner type="error" isDismissible={false} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} fontSize="18px">
                    {PENDING_CANCELLATION.BANNER_TEXT.TITLE}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }} fontSize="16px">
                    {PENDING_CANCELLATION.BANNER_TEXT.MESSAGE}
                  </Typography>
                </Banner>
              </Grid>
            )}
            <Typography {...typographyStyles.sectionTitle}>Greiðslusamkomulag</Typography>
            <Grid xs={12} sm={12}>
              <Agreements agreements={agreements} />
            </Grid>
          </>
        )}
      </Grid>

      {operationError && (
        <Paper elevation={1}>
          <Error error={operationError} />
        </Paper>
      )}

      <Box>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isMobile ? 'flex-start' : 'center'}
          sx={{ mt: '60px' }}
        >
          {isLoading ? (
            <Skeleton variant="text" width="40%" height={60} sx={{ p: '8px 8px 8px 0' }} />
          ) : (
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <Typography
                {...typographyStyles.sectionTitle}
                sx={{
                  ...typographyStyles.sectionTitle.sx,
                  mt: 0,
                  p: '8px 8px 8px 0',
                }}
              >
                Til innheimtu
              </Typography>
              <Typography
                sx={{
                  ...typographyStyles.sectionTitle.sx,
                  mt: isMobile ? '0' : 0,
                  ml: isMobile ? '0' : '9px',
                  p: '8px 8px 8px 0',
                }}
              >
                {formatAmountToISKCurrencyString(totalRemaining)}
              </Typography>
            </Box>
          )}
        </Box>

        {isIssueDrawerOpen && (
          <CreateIssueSideBar
            closeCreateIssueDrawer={() => {
              setIsIssueDrawerOpen(false);
              setPreSelectedIssueType(null);
            }}
            claims={claims.filter((x) => selectedClaims.includes(x.claimId))}
            preSelectedIssueType={preSelectedIssueType}
            hasContactInfo={user.settings.hasContactInfo}
          />
        )}

        {error && (
          <Box sx={{ mt: '16px' }}>
            <Error error={error} />
          </Box>
        )}
      </Box>

      {isLoading ? (
        <PayerClaimsSkeleton />
      ) : (
        !error &&
        claimants.map((claimant) => {
          const claimantClaims = claims.filter((claim) => claim.claimantName === claimant);
          const claimTotalRemaining = _.sumBy(claimantClaims, 'totalRemaining');

          if (filterBy !== '' && claimant !== filterBy) return null;

          return (
            <React.Fragment key={claimant}>
              <Box
                sx={{
                  mt: isMobile ? '35px' : '40px',
                  mb: '25px',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: {
                      xs: '18px',
                      sm: '24px',
                    },
                    fontWeight: 500,
                  }}
                >
                  {claimant}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: {
                      xs: '18px',
                      sm: '24px',
                    },
                    fontWeight: 500,
                    mt: isMobile ? '8px' : '0',
                  }}
                >
                  {formatAmountToISKCurrencyString(claimTotalRemaining)}
                </Typography>
              </Box>
              <Paper elevation={1}>
                <PayerClaims
                  claims={claimantClaims}
                  noClaimsText={`Þú átt engin opin mál í lögfræðiinnheimtu Lögheimtunnar. ${
                    filterBy !== '' && 'Athugadu að þetta yfirlit er síað á ákvedinn kröfuhafa.'
                  }`}
                  criteria={sortCriteria}
                  updateCriteria={setSortCriteria}
                  isSelectable={true}
                  selected={selectedClaims}
                  onSelectedChange={handleSelectedClaimsChange}
                  hideActions={true}
                  defaultCaseNumbers={new Set(defaults.map((d) => d.caseNumber))}
                />
                <Box sx={{ p: 2 }}>
                  <ClaimantActions
                    claimantClaims={claimantClaims}
                    selectedClaims={selectedClaims}
                    setOperationError={setOperationError}
                    onIssueDrawerOpen={setIsIssueDrawerOpen}
                    setPreSelectedIssueType={setPreSelectedIssueType}
                  />
                </Box>
              </Paper>
            </React.Fragment>
          );
        })
      )}
      {!isLoading && !error && claims.length > 0 && (
        <Box sx={{ mt: isMobile ? '0px' : 0, width: isMobile ? '100%' : 'auto' }}>
          <PayerClaimsActions
            selectedClaims={selectedClaims}
            claims={claims}
            onIssueDrawerOpen={setIsIssueDrawerOpen}
            preSelectedIssueType={preSelectedIssueType}
            setPreSelectedIssueType={setPreSelectedIssueType}
            setOperationError={setOperationError}
          />
        </Box>
      )}
    </>
  );
}
